// Combines BOTH of the agentic/new-backend pipeline's `[[...]]` marker grammars into one
// `replaceAgenticMarkersInNode` pass over already-rendered `react-markdown` text nodes — mirrors
// `../../../components/messageRows/markdown/CitationsMarkdownComponents.js`'s
// `replaceCitationMarkersInNode` shape/recursion exactly, but recognizes this pipeline's own two
// marker kinds instead of the legacy `[[citations:JSON]]` marker:
//   - `[[type:len:payload]]` (document/entity/citation inline-component chips) — decoded via the
//     SAME byte-length-safe `decodeInlineComponentsToSegments` the rest of this feature already
//     uses (see `../../../services/inlineComponents/decodeInlineComponentStream.js`'s header
//     comment for why this must never become a naive delimiter scan).
//   - `[[cite:citationId]]` / `[[cite:citationId1,citationId2]]` (`../../../../../../dash-api/
//     services/ai-assistant/src/analysis/synthesize.ts`'s inline citation markers) — resolved
//     against the run's `citations: {citationId, reportId, segmentId}[]` array into the exact
//     `{items: [{uid, indexPath}]}` payload shape `InlineCitationsTrigger`/`CitationsPayloadDialog`
//     already expect, so the SAME pill+dialog UI the legacy pipeline uses for `[[citations:...]]`
//     is reused here rather than re-invented. A marker whose id(s) don't all resolve against
//     `citations` is left as literal text (never a broken/error chip) — matches the legacy
//     pipeline's own "no items" fallback exactly.
// This module intentionally only handles STRING leaves; the parent-node recursion at the bottom
// (array/element walking) is identical in shape to `replaceCitationMarkersInNode` on purpose, so
// the two pipelines stay easy to compare — this is NOT shared/imported between them because their
// marker grammars are genuinely different backends' formats, not the same thing renamed.
import React from 'react';
import InlineComponentChip from '../InlineComponentChip';
import {InlineCitationsTrigger} from '../../../components/messageRows/markdown/CitationsMarkdownComponents';
import {
    decodeInlineComponentsToSegments,
    polishInlineSegmentsForDisplay,
    stripTechnicalEntityRefs,
} from '../../../services/inlineComponents/decodeInlineComponentStream';

export const CITE_MARKER_REGEX = /\[\[cite:([\w,-]+)]]/g;

/** Private-use placeholders — react-markdown must never see raw `[[type:len:{json}]]`. */
const PROTECTED_CHIP_PREFIX = '\uE000IC';
const PROTECTED_CHIP_SUFFIX = '\uE000';
const PROTECTED_CHIP_TOKEN_RE = /\uE000IC(\d+)\uE000/g;

/**
 * Decode complete inline-component markers and replace them with opaque tokens so
 * markdown emphasis / links inside JSON payloads cannot fragment the chip.
 *
 * @param {string} text
 * @returns {{protectedText: string, chips: Array<{token: string, type: string, payload: unknown}>}}
 */
export function protectInlineComponentMarkers(text) {
    if (text == null || text === '') {
        return {protectedText: '', chips: []};
    }
    const source = String(text);
    if (!source.includes('[[')) {
        return {protectedText: source, chips: []};
    }
    const {segments} = decodeInlineComponentsToSegments(source);
    const chips = [];
    let protectedText = '';
    for (const segment of segments) {
        if (segment.type === 'text') {
            protectedText += segment.value;
            continue;
        }
        if (segment.type === 'kb') {
            continue;
        }
        const token = `${PROTECTED_CHIP_PREFIX}${chips.length}${PROTECTED_CHIP_SUFFIX}`;
        chips.push({token, type: segment.type, payload: segment.payload});
        protectedText += token;
    }
    return {protectedText, chips};
}

/**
 * Resolves a `[[cite:id1,id2]]` marker's comma-separated ids against `citations`
 * (`{citationId, reportId, segmentId}[]`) into the `InlineCitationsTrigger`/`CitationsPayloadDialog`
 * `{items: [{uid, indexPath}]}` shape. An id with no matching `citations` entry is silently
 * dropped from `items` (never invented) — callers treat an empty result as "leave marker as
 * literal text", exactly like the legacy pipeline's `parseCitationMarkerPayload` + `!items.length`
 * check.
 */
export function resolveCiteMarkerItems(idsText, citations) {
    const ids = String(idsText || '').split(',').map((id) => id.trim()).filter(Boolean);
    const citationList = Array.isArray(citations) ? citations : [];

    return ids
        .map((id) => citationList.find((citation) => citation && String(citation.citationId) === id))
        .filter(Boolean)
        .map((citation) => ({
            uid: citation.reportId == null ? '' : String(citation.reportId),
            indexPath: citation.segmentId == null ? '' : String(citation.segmentId),
        }));
}

function replaceCiteMarkersInText(text, {citations, onOpenCitations}, keyPrefix) {
    const matches = Array.from(text.matchAll(new RegExp(CITE_MARKER_REGEX)));
    if (matches.length === 0) {
        return [text];
    }

    const result = [];
    let lastIndex = 0;

    matches.forEach((match, index) => {
        const fullMatch = match[0] || '';
        const idsText = match[1] || '';
        const matchIndex = match.index || 0;

        if (matchIndex > lastIndex) {
            result.push(text.slice(lastIndex, matchIndex));
        }

        const items = resolveCiteMarkerItems(idsText, citations);
        if (items.length === 0) {
            result.push(fullMatch);
        } else {
            result.push(
                <InlineCitationsTrigger
                    key={`${keyPrefix}-cite-${index}`}
                    payload={{items}}
                    onClick={onOpenCitations}
                />
            );
        }

        lastIndex = matchIndex + fullMatch.length;
    });

    if (lastIndex < text.length) {
        result.push(text.slice(lastIndex));
    }

    return result;
}

function replaceDecodedMarkersInText(text, context, keyPrefix) {
    if (!text.includes('[[')) {
        const cleaned = stripTechnicalEntityRefs(text);
        return cleaned ? [cleaned] : [];
    }

    const {segments} = decodeInlineComponentsToSegments(text);
    const displaySegments = polishInlineSegmentsForDisplay(segments);

    const result = [];
    displaySegments.forEach((segment, segmentIndex) => {
        if (segment.type === 'text') {
            result.push(...replaceCiteMarkersInText(segment.value, context, `${keyPrefix}-${segmentIndex}`));
        } else {
            result.push(
                <InlineComponentChip
                    key={`${keyPrefix}-component-${segmentIndex}`}
                    type={segment.type}
                    payload={segment.payload}
                    onSelect={context.onSelectComponent}
                />
            );
        }
    });
    return result;
}

function restoreProtectedChipsInText(text, context, keyPrefix) {
    const chips = context?.protectedChips || [];
    const result = [];
    let lastIndex = 0;
    let match;
    let part = 0;
    const re = new RegExp(PROTECTED_CHIP_TOKEN_RE);
    while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) {
            result.push(...replaceDecodedMarkersInText(
                text.slice(lastIndex, match.index),
                context,
                `${keyPrefix}-t${part++}`
            ));
        }
        const chip = chips[Number(match[1])];
        if (chip) {
            result.push(
                <InlineComponentChip
                    key={`${keyPrefix}-protected-${match[1]}`}
                    type={chip.type}
                    payload={chip.payload}
                    onSelect={context.onSelectComponent}
                />
            );
        }
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
        result.push(...replaceDecodedMarkersInText(
            text.slice(lastIndex),
            context,
            `${keyPrefix}-t${part}`
        ));
    }
    return result;
}

/**
 * @param {unknown} node a `react-markdown` element's `children` (string, array, or element).
 * @param {{citations?: Array, onSelectComponent?: Function, onOpenCitations?: Function, protectedChips?: Array}} context
 * @param {string} [keyPrefix]
 */
export function replaceAgenticMarkersInNode(node, context, keyPrefix = 'node') {
    if (typeof node === 'string') {
        if (context?.protectedChips?.length && node.includes(PROTECTED_CHIP_PREFIX)) {
            return restoreProtectedChipsInText(node, context, keyPrefix);
        }
        if (!node.includes('[[')) {
            return stripTechnicalEntityRefs(node);
        }
        const decoded = replaceDecodedMarkersInText(node, context, keyPrefix);
        return decoded.length === 1 && typeof decoded[0] === 'string' ? decoded[0] : decoded;
    }

    if (Array.isArray(node)) {
        return node.flatMap((child, index) => replaceAgenticMarkersInNode(child, context, `${keyPrefix}-${index}`));
    }

    if (React.isValidElement(node) && node.props?.children) {
        return React.cloneElement(node, {
            ...node.props,
            children: replaceAgenticMarkersInNode(node.props.children, context, keyPrefix),
        });
    }

    return node;
}

export default replaceAgenticMarkersInNode;
